import { useGenericFormStore } from '@neo/blocks/forms/persistance-store'
import { type InfiniteData, useMutation } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { produce } from 'immer'
import { uniqBy } from 'lodash'
import { useNavigate } from 'router-v6'
import { queryClient } from 'v2/configs/rq-setup'
import { conversationService } from 'v2/shared/services/conversations-service'
import { executionService } from 'v2/shared/services/execution-service'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import {
  conversationMutationKeys,
  conversationQueryKeys,
} from 'v2/shared/utils/query-key-management'
import { usePhoneStore } from 'v2/state/phone'
import { Views, TaskViewName } from 'v2/types'
import {
  type Channels,
  type ChunkPayload,
  ConvChannels,
  type DownloadMultipleRecordingsPayload,
  type ExportViewPayload,
  type RActiveConversation,
  type RNewConversation,
  type RRecord,
  type RView,
  type RViews,
  type TransferPayload,
  type ViewCreatePayload,
} from 'v2/types/api/conversations'
import { CallDirection, ParticipantState } from 'v2/types/call'
import { ChunkOrigin } from 'v2/types/chunk.external'
import type { Chunk, RConversationDetailInternal } from 'v2/types/chunk.internal'
import { PubnubEvents } from 'v2/types/pubnub'
import { pubnubEventBus } from 'v2/utils/event-bus/pubnub'
import { logger } from 'v2/utils/logger'
import { errorToast } from 'v2/utils/toasts'
import { useTaskStore } from 'v2/state/task'
import {
  getAgentSip,
  getConversationURL,
  getOutgoingCallDirection,
  parseConversationURL,
} from '../utils'
import { toChunkModel } from '../utils/transformers'
import { isActiveConversation, isNewConversation } from '../utils/typeguards'

export const useConversationMutations = () => {
  const navigate = useNavigate()
  const updateConversationMutation = useMutation({
    mutationFn: async (data: {
      conversationNumber: string
      payload: Partial<RActiveConversation>
    }) => {
      await conversationService.updateConversation(data.payload, data.conversationNumber)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: conversationQueryKeys().active })
    },
  })

  const addActiveConversation = (data: RActiveConversation) => {
    queryClient.setQueryData<RActiveConversation[]>(conversationQueryKeys().active, (oldData) =>
      uniqBy([data, ...(oldData || [])], 'conversation_number'),
    )
    usePhoneStore.getState().setCallConversation() // To find and set the parent conversation for the ongoing call
  }

  const updateActiveConversation = ({
    conversationNumber,
    data,
  }: {
    conversationNumber: string
    data: Partial<RActiveConversation>
  }) => {
    queryClient.setQueryData<RActiveConversation[]>(conversationQueryKeys().active, (oldData) =>
      produce(oldData, (draft) => {
        if (!draft) return

        const conversationIndex = draft.findIndex(
          (conv) => conv.conversation_number === conversationNumber,
        )

        if (conversationIndex !== -1) {
          draft[conversationIndex] = {
            ...draft[conversationIndex],
            ...data,
          }
        }
      }),
    )
    // Update the call conversation in the phone store with the updated conversation
    usePhoneStore.getState().setCallConversation()
  }

  const removeActiveConversation = (conversationNumber: string) => {
    queryClient.setQueryData<RActiveConversation[]>(
      conversationQueryKeys().active,
      (oldData = []) => {
        const newData = oldData.filter((conv) => conv.conversation_number !== conversationNumber)
        // CONV_NAVIGATION_REDIRECT
        if (parseConversationURL().viewId === Views.active) {
          if (newData.length === 0) navigate(getConversationURL(''))
          else navigate(getConversationURL(newData[0].conversation_number), { replace: true })
        }
        return newData
      },
    )
    queryClient.removeQueries({ queryKey: conversationQueryKeys().chunks(conversationNumber) })
    useGenericFormStore.getState().clearAllSimilarKeys(conversationNumber)
  }

  const removeInvitedConversation = (contactUuid: string) => {
    queryClient.setQueryData<RNewConversation[]>(
      conversationQueryKeys().invited,
      (oldData = []) => {
        const newData = oldData.filter((conv) => conv.contact_uuid !== contactUuid)
        // CONV_NAVIGATION_REDIRECT
        if (parseConversationURL().viewId === Views.unassigned) {
          if (newData.length === 0) navigate(getConversationURL(''))
          else navigate(getConversationURL(newData[0].conversation_number), { replace: true })
        }
        return newData
      },
    )
  }

  const taskViewFilters = useTaskStore((state) => state.viewFilters[TaskViewName.Unassigned])

  const acceptConversationMutation = useMutation({
    mutationFn: async (data: RNewConversation) => {
      if (data.invite_type === 'TRANSFER') {
        await executionService.acceptAsyncTransfer(
          data.contact_uuid,
          data.channel,
          data.customer_id || data.customerId,
        )
      } else {
        await executionService.acceptNewConversation(
          data.contact_uuid,
          data.channel,
          data.customer_id || data.customerId,
          data.is_agent_created_task,
        )
      }
    },
    onSuccess: (_data, variables) => {
      removeInvitedConversation(variables.contact_uuid)
      queryClient.invalidateQueries({
        queryKey: conversationQueryKeys().tasks(TaskViewName.Unassigned, taskViewFilters),
      })
    },
    onError: (error) => {
      logger.error('Error accepting conversation', error)
    },
  })

  const resolveConversationMutation = useMutation({
    mutationFn: async (data: { conversationNumber: string; payload: ChunkPayload }) => {
      await conversationService.addChunk(data.payload, data.conversationNumber)
    },
    retry: false,
    onSuccess: (_, variables) => {
      // queryClient.setQueryData<RActiveConversation[]>(conversationQueryKeys().active, (oldData) =>
      // produce(oldData, (draftState) => {
      //   if (!draftState) return
      //   const conversationIndex = draftState?.findIndex(
      //     (conv) => conv.conversation_number === p.conversationNumber,
      //   )
      //   if (conversationIndex !== -1) {
      //     draftState[conversationIndex].child_conversations[
      //       draftState[conversationIndex].child_conversations.length - 1
      //     ].state = ConversationStatus.IN_WRAP_UP
      //   }
      // }),
      // )
      queryClient.invalidateQueries({ queryKey: conversationQueryKeys().active })
      queryClient.invalidateQueries({
        queryKey: conversationQueryKeys().conversationDetail(variables.conversationNumber),
      })
    },
    onError: (error) => {
      logger.error('Error adding chunk', error)
    },
  })

  const rejectConversationMutation = useMutation({
    mutationFn: async (data: {
      contactUuid: string
      channel: Channels
      isAgentCreatedTask?: boolean
    }) => {
      await executionService.declineNewConversation(
        data.contactUuid,
        data.channel,
        data.isAgentCreatedTask,
      )
    },
    onSuccess: (_data, variables) => {
      removeInvitedConversation(variables.contactUuid)
      queryClient.invalidateQueries({
        queryKey: conversationQueryKeys().tasks(TaskViewName.Unassigned, taskViewFilters),
      })
      // Optionally, you can add any additional logic here, such as showing a notification
    },
    onError: (error) => {
      logger.error('Error rejecting conversation', error)
    },
  })

  return {
    addActiveConversation,
    removeActiveConversation,
    removeInvitedConversation,
    acceptConversationMutation,
    updateConversationMutation,
    resolveConversationMutation,
    rejectConversationMutation,
    updateActiveConversation,
  }
}

export const useTransferMutations = () => {
  const agentSip = getAgentSip()

  let directionInbound = false
  const callDirection = usePhoneStore.getState().activeCall?.direction
  directionInbound =
    getOutgoingCallDirection(usePhoneStore.getState().activeCall?.meta, callDirection) ===
    CallDirection.INBOUND

  const cancelTransfer = useMutation({
    mutationFn: async (data: {
      channel?: Channels
      transferType: 'cold' | 'warm'
      contactUuid?: string
      member?: string
    }) => {
      if (!data.channel) throw new Error('Channel is required')
      if (!data.contactUuid) throw new Error('Contact UUID is required')
      if (!agentSip) throw new Error('Agent SIP not found')
      // Call transfer is handled differently
      if (data.channel === ConvChannels.CALL)
        await conversationService.cancelCallTransfer(
          {
            agent_sip: agentSip,
            contact_uuid: data.contactUuid,
            direction_inbound: directionInbound,
          },
          data.transferType,
        )
      else
        await executionService.cancelTransfer(data.channel, data.transferType, {
          agent_sip: agentSip,
          contact_uuid: data.contactUuid,
        })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: conversationQueryKeys().active })
      usePhoneStore.getState().clearParticipants()
    },
  })

  const transfer = useMutation({
    mutationKey: conversationMutationKeys().transfer,
    mutationFn: async (data: {
      channel: Channels
      transferType: 'cold' | 'warm'
      contactUuid: string
      identifier: string
      agentOrTeam: 'agent' | 'team' | 'external number'
      member?: string
    }) => {
      if (!agentSip) throw new Error('Agent SIP not found')

      // direction_inbound is only applicable for call channels i presume, if it is for all channels, handle it here

      const payload: TransferPayload = {
        contact_uuid: data.contactUuid,
        transfer_type: data.agentOrTeam,
        identifier: data.identifier,
        agent_sip: agentSip,
        push_contact_details_async_flag: false,
        direction_inbound: directionInbound,
        member: data.member,
      }

      if (data.channel === ConvChannels.CALL)
        await conversationService.initiateCallTransfer(payload, data.transferType)
      else await executionService.transfer(data.channel, data.transferType, payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: conversationQueryKeys().active })
    },
    onError: (error, variables) => {
      errorToast({
        title:
          variables.channel === ConvChannels.CALL
            ? 'Failed to invite participant'
            : 'Failed to transfer',
        description: getErrorText(error as AxiosError) || 'Please try again.',
      })
      logger.error('Error performing transfer', error)
    },
  })

  const leaveTransfer = useMutation({
    mutationFn: async (data: {
      contactUuid?: string
      leaveType: 'leave_primary' | 'drop_secondary' | 'close_conversation' | 'leave_secondary'
      channel?: string
    }) => {
      if (!data.contactUuid || !data.channel)
        throw new Error('Contact UUID and channel are required')
      await executionService.leaveTransfer(data.contactUuid, data.leaveType, data.channel)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: conversationQueryKeys().active })
    },
    onError: (error) => {
      logger.error('Error performing leave transfer', error)
    },
  })

  // merge participant into the main call w customer
  const mergeParticipant = useMutation({
    mutationFn: async (data: Partial<TransferPayload>) => {
      const isExternal = !!usePhoneStore
        .getState()
        .participants.find((p) => p.participantType === 'external')
      await conversationService.mergeParticipant(
        {
          contact_uuid: data.contact_uuid,
          agent_sip: agentSip,
          direction_inbound: directionInbound,
        },
        isExternal,
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: conversationQueryKeys().active })
      usePhoneStore.getState().updateParticipant(usePhoneStore.getState().participants[0].member, {
        state: ParticipantState.MERGED,
      })
    },
    onError: (error) => {
      logger.error('Error performing merge participant', error)
    },
  })

  // switch between customer leg and participant leg
  const toggleCall = useMutation({
    mutationFn: async (data: Partial<TransferPayload>) => {
      await conversationService.toggleCall({
        contact_uuid: data.contact_uuid,
        agent_sip: agentSip,
        direction_inbound: directionInbound,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: conversationQueryKeys().active })
    },
    onError: (error) => {
      logger.error('Error performing toggle call', error)
    },
  })

  const hangupParticipant = useMutation({
    mutationFn: async (data: Partial<TransferPayload>) => {
      await conversationService.hangupParticipant({
        contact_uuid: data.contact_uuid,
        agent_sip: agentSip,
        direction_inbound: directionInbound,
        member: usePhoneStore.getState().participants[0].member,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: conversationQueryKeys().active })
    },
    onError: (error) => {
      logger.error('Error performing hangup participant', error)
    },
  })

  return {
    cancelTransfer,
    transfer,
    leaveTransfer,
    mergeParticipant,
    toggleCall,
    hangupParticipant,
  }
}

// Update chunk in query cache
// Receive a find function and an update function
// Find the chunk using the find function
// Update the chunk using the update function
// Update the chunk in the query cache
export const updateChunkInQueryCache = (
  conversationNumber: string,
  findFn: (chunk: Chunk) => boolean,
  updateFn: (chunk: Chunk) => Partial<Chunk>,
  isLikelyInLastPage = true,
) => {
  const updater = () =>
    queryClient.setQueryData<InfiniteData<Chunk[]>>(
      conversationQueryKeys().chunks(conversationNumber),
      (oldData) => {
        try {
          if (!oldData) return oldData

          return produce(oldData, (draft) => {
            const startIndex = isLikelyInLastPage ? draft.pages.length - 1 : 0
            const endIndex = isLikelyInLastPage ? draft.pages.length : -1
            const step = isLikelyInLastPage ? -1 : 1

            for (let i = startIndex; i !== endIndex; i += step) {
              if (!draft.pages[i]) break
              const chunkIndex = draft.pages[i]?.findIndex(findFn)
              if (chunkIndex !== -1) {
                // Merge the partial update with the existing chunk
                draft.pages[i][chunkIndex] = {
                  ...draft.pages[i][chunkIndex],
                  ...updateFn(draft.pages[i][chunkIndex]),
                }
                return // Exit the loop once the chunk is found and updated
              }
            }
          })
        } catch (error) {
          logger.error('Error updating chunk in query cache', error)
        }
        return oldData
      },
    )

  if (
    queryClient.getQueryState(conversationQueryKeys().chunks(conversationNumber))?.status ===
    'pending'
  )
    pubnubEventBus.once(PubnubEvents.CHUNKS_FETCHED, (d) => {
      if (d.conversationNumber === conversationNumber) updater()
    })
  else updater()
}

export const addChunkToQueryCache = (newChunk: Chunk, conversationNumber: string) => {
  if (conversationNumber) {
    const updater = () => {
      queryClient.setQueryData<InfiniteData<Chunk[]>>(
        conversationQueryKeys().chunks(conversationNumber),
        (oldData) => {
          if (!oldData) return oldData

          return produce(oldData, (draft) => {
            const lastPageIndex = draft.pages.length - 1
            const lastPage = draft.pages[lastPageIndex]

            const existingChunkIndex = lastPage.findIndex(
              (chunk) => chunk.id === newChunk.id || chunk.timetoken === newChunk.timetoken,
            )

            if (existingChunkIndex !== -1) {
              // Replace the existing chunk
              lastPage[existingChunkIndex] = newChunk
            } else {
              // Add the new chunk if it doesn't exist
              lastPage.push(newChunk)
            }

            // Sort the last page by timetoken
            lastPage.sort((a, b) => String(a.timetoken).localeCompare(String(b.timetoken)))
          })
        },
      )

      // Update live & old conversations as well
      queryClient.setQueryData<RConversationDetailInternal>(
        conversationQueryKeys().conversationDetail(conversationNumber),
        (oldData) => {
          if (!oldData) return oldData
          return produce(oldData, (draft) => {
            draft.chunks.push(newChunk)
          })
        },
      )
    }
    if (
      queryClient.getQueryState(conversationQueryKeys().chunks(conversationNumber))?.status ===
      'pending'
    )
      pubnubEventBus.once(PubnubEvents.CHUNKS_FETCHED, (d) => {
        if (d.conversationNumber === conversationNumber) updater()
      })
    else updater()
  }
}

export const useAddChunkMutation = () =>
  useMutation({
    mutationFn: async (data: {
      payload: Partial<ChunkPayload>
      conversation: RActiveConversation | RNewConversation | RRecord
    }) => {
      const convNumber =
        isActiveConversation(data.conversation) || isNewConversation(data.conversation)
          ? data.conversation.conversation_number
          : data.conversation.conv_number
      const chunk = await conversationService.addChunk(data.payload, convNumber)
      return chunk
    },
    onSuccess: (d, { conversation }) => {
      const convNumber =
        isActiveConversation(conversation) || isNewConversation(conversation)
          ? conversation.conversation_number
          : conversation.conv_number
      addChunkToQueryCache(toChunkModel(d.data, ChunkOrigin.apiFetchChunks), convNumber)
      return d.data
    },
  }) // Handle all mutations related to conversations
export const useCreateViewMutation = () =>
  useMutation({
    mutationFn: async (payload: ViewCreatePayload) => {
      const chunk = await conversationService.createView(payload)
      return chunk
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: conversationQueryKeys().views() })
    },
  })

export const useUpdateViewMutation = (uuid: string) =>
  useMutation({
    mutationFn: async (payload: ViewCreatePayload) => {
      const chunk = await conversationService.updateView({
        uuid,
        payload,
      })
      return chunk
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: conversationQueryKeys().views() })
      queryClient.invalidateQueries({
        queryKey: conversationQueryKeys().views(uuid),
      })
    },
    onError: (error) => {
      errorToast({
        title: 'Failed to update view',
        description: error.message || 'Please try again.',
      })
    },
  })

export const useExportViewMutation = (uuid: string) =>
  useMutation({
    mutationFn: async (payload: ExportViewPayload) => {
      const chunk = await conversationService.exportView({
        uuid,
        payload,
      })
      return chunk
    },
    onSuccess: () => {},
  })

export const useDownloadRecordingsMutation = () =>
  useMutation({
    mutationFn: (payload: DownloadMultipleRecordingsPayload) =>
      conversationService.downloadRecordings(payload),
  })

export const useFavoriteViewMutation = () =>
  useMutation({
    mutationFn: async ({ uuid, favourite }: { uuid: string; favourite: boolean }) => {
      const response = await conversationService.favoriteView({
        uuid,
        payload: { favourite },
      })
      return response
    },
    onMutate: async ({ uuid, favourite }) => {
      // Get the current cache for views
      const previousViews: RViews | undefined = queryClient.getQueryData(
        conversationQueryKeys().views(),
      )

      // If there are no views cached, return an empty object to handle this edge case
      if (!previousViews) {
        return { previousViews: {} }
      }

      // Destructure the previous views data
      const {
        favourite: favouriteViews,
        private: privateViews,
        public: publicViews,
        shared: sharedViews,
      } = previousViews

      // Handle the optimistic update based on the 'favourite' flag
      let updatedFavouriteViews = [...favouriteViews]

      if (favourite) {
        // If favouriting, find the view in private, public, or shared, and add it to favourites
        let viewToAdd: RView | undefined

        // Check in the private, public, and shared arrays for the view to add
        const allViews = [...privateViews, ...publicViews, ...sharedViews]
        viewToAdd = allViews.find((view) => view.uuid === uuid)

        // If the view is found and not already in the favourite array, add it
        if (viewToAdd && !updatedFavouriteViews.some((view) => view.uuid === uuid)) {
          updatedFavouriteViews = [...updatedFavouriteViews, viewToAdd]
        }
      } else {
        // If unfavouriting, remove the view from the favourite array
        updatedFavouriteViews = updatedFavouriteViews.filter((view) => view.uuid !== uuid)
      }

      // Update the cache with the new favourite list
      queryClient.setQueryData(conversationQueryKeys().views(), {
        favourite: updatedFavouriteViews,
        private: privateViews,
        public: publicViews,
        shared: sharedViews,
      })

      // Return the previous data for rollback if mutation fails
      return {
        previousViews,
      }
    },
    onError: (error, _variables, context) => {
      // If the mutation failed, rollback to the previous views
      if (context?.previousViews) {
        queryClient.setQueryData(conversationQueryKeys().views(), context.previousViews)
      }
      logger.error('Error favoriting view', error)
    },
    onSettled: () => {
      // Invalidate the views query to ensure fresh data is fetched from the server
      queryClient.invalidateQueries({ queryKey: conversationQueryKeys().views() })
    },
  })
